import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AdminLayout from '../../components/AdminLayout';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: #333;
  font-weight: 600;
  margin: 0;
`;

const Description = styled.p`
  color: #666;
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: white;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background: #f5f5f5;
      border-color: #ccc;
    }
  `}
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem;
  color: #333;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1rem;
  background: ${props => props.enabled ? '#e8f5e9' : '#f5f5f5'};
  border-radius: 8px;
  border: 2px solid ${props => props.enabled ? '#4caf50' : '#ddd'};
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.enabled ? '#388e3c' : '#bbb'};
  }

  input {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  span {
    font-weight: 500;
    color: ${props => props.enabled ? '#2e7d32' : '#666'};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${props => props.active ? '#e8f5e9' : '#f5f5f5'};
  color: ${props => props.active ? '#2e7d32' : '#666'};
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? '#4caf50' : '#999'};
  animation: ${props => props.active ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const NextBackupInfo = styled.div`
  background: #f0f7ff;
  border: 1px solid #b8daff;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #004085;
`;

const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color || '#667eea'}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #333;
    font-size: 0.875rem;
  }
  
  td {
    color: #666;
    font-size: 0.9rem;
  }
  
  tr:hover td {
    background: #f8f9fa;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SmallButton = styled.button`
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${props => props.danger ? '#dc3545' : props.warning ? '#ffc107' : '#667eea'};
  color: ${props => (props.danger || props.warning) ? '#333' : 'white'};
  
  &:hover {
    opacity: 0.85;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  color: #333;
  font-size: 1.25rem;
`;

const ModalText = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: #f5f5f5;
  color: #333;
  border: none;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: ${props => props.danger ? '#dc3545' : '#667eea'};
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    opacity: 0.9;
  }
`;

const InfoBox = styled.div`
  background: #f0f7ff;
  border: 1px solid #b8daff;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  h4 {
    margin: 0 0 0.5rem;
    color: #004085;
    font-size: 0.9rem;
  }
  
  ul {
    margin: 0;
    padding-left: 1.25rem;
    color: #004085;
    font-size: 0.85rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
`;

const RestoreOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #667eea;
    background: #f8f9ff;
  }
  
  input:checked + & {
    border-color: #667eea;
    background: #f0f7ff;
  }
`;

const CollectionInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const CollectionItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #999;
`;

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const BackupPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreMode, setRestoreMode] = useState('replace');
  const [restoring, setRestoring] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  
  // 自动备份设置状态
  const [autoBackup, setAutoBackup] = useState({
    enabled: false,
    schedule: 'daily',
    dayOfWeek: 1,
    hour: 2,
    minute: 0,
    retentionDays: 30,
    lastBackup: null,
    nextBackup: null
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/backup/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setBackups(data.data);
      }
    } catch (error) {
      console.error('获取备份列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取自动备份设置
  const fetchAutoBackupSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/backup/auto-backup/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAutoBackup(data.data);
      }
    } catch (error) {
      console.error('获取自动备份设置失败:', error);
    }
  };

  useEffect(() => {
    fetchBackups();
    fetchAutoBackupSettings();
  }, []);

  // 保存自动备份设置
  const handleSaveAutoBackup = async () => {
    try {
      setSavingSettings(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/backup/auto-backup/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(autoBackup)
      });
      const data = await response.json();
      if (data.success) {
        setAutoBackup(data.data);
        alert('自动备份设置已保存');
      } else {
        alert('保存失败: ' + data.message);
      }
    } catch (error) {
      console.error('保存设置失败:', error);
      alert('保存失败，请稍后重试');
    } finally {
      setSavingSettings(false);
    }
  };

  // 手动触发立即备份
  const handleTriggerBackup = async () => {
    try {
      setBackupLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/backup/auto-backup/trigger', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        alert('立即备份成功！');
        fetchBackups();
        fetchAutoBackupSettings();
      } else {
        alert('备份失败: ' + data.message);
      }
    } catch (error) {
      console.error('备份失败:', error);
      alert('备份失败，请稍后重试');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/backup/backup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        alert(`备份成功！\n文件名: ${data.data.filename}\n集合数: ${data.data.collectionsCount}\n大小: ${formatFileSize(data.data.size)}`);
        fetchBackups();
      } else {
        alert('备份失败: ' + data.message);
      }
    } catch (error) {
      console.error('备份失败:', error);
      alert('备份失败，请稍后重试');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/backup/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errText = await response.text();
        alert(`下载失败 (${response.status}): ${errText}`);
        return;
      }
      
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json') && !contentType.includes('application/octet-stream')) {
        const text = await response.text();
        if (text.startsWith('{')) {
          const errData = JSON.parse(text);
          alert(`下载失败: ${errData.message || '未知错误'}`);
          return;
        }
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        alert('下载失败: 文件内容为空');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载失败:', error);
      alert('下载失败: ' + error.message);
    }
  };

  const handleShowDetail = async (filename) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/backup/info/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedBackup(data.data);
        setDetailModal(true);
      }
    } catch (error) {
      console.error('获取详情失败:', error);
      alert('获取详情失败');
    }
  };

  const handleRestore = async () => {
    if (!restoreModal) return;
    
    const confirmed = window.confirm(
      restoreMode === 'replace' 
        ? '⚠️ 警告：此操作将清空现有数据并用备份数据替换！\n\n确定要继续吗？'
        : '此操作将合并备份数据到现有数据中（跳过重复记录）。\n\n确定要继续吗？'
    );
    
    if (!confirmed) return;
    
    try {
      setRestoring(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/backup/restore/${restoreModal}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ replace: restoreMode === 'replace' })
      });
      const data = await response.json();
      if (data.success) {
        alert(`恢复成功！\n成功: ${data.data.successCount} 个集合\n失败: ${data.data.errorCount} 个集合`);
        setRestoreModal(null);
      } else {
        alert('恢复失败: ' + data.message);
      }
    } catch (error) {
      console.error('恢复失败:', error);
      alert('恢复失败，请稍后重试');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async (filename) => {
    const confirmed = window.confirm(`确定要删除备份文件 "${filename}" 吗？`);
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/backup/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        alert('备份文件已删除');
        fetchBackups();
      } else {
        alert('删除失败: ' + data.message);
      }
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败，请稍后重试');
    }
  };

  const totalSize = backups.reduce((sum, b) => sum + b.size, 0);

  return (
    <AdminLayout title="数据备份">
      <PageContainer>
        <Header>
          <div>
            <Title>数据备份</Title>
            <Description>备份和恢复系统数据，保护您的数据安全</Description>
          </div>
        </Header>

        <StatsCards>
          <StatCard>
            <StatIcon color="#667eea">📦</StatIcon>
            <StatInfo>
              <StatValue>{backups.length}</StatValue>
              <StatLabel>备份文件</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon color="#28a745">💾</StatIcon>
            <StatInfo>
              <StatValue>{formatFileSize(totalSize)}</StatValue>
              <StatLabel>总大小</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon color="#17a2b8">🗄️</StatIcon>
            <StatInfo>
              <StatValue>{backups.length > 0 ? 'MongoDB' : '-'}</StatValue>
              <StatLabel>数据库类型</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsCards>

        {/* 自动备份设置 */}
        <Section>
          <SectionTitle>⚙️ 自动备份设置</SectionTitle>
          
          <Toggle 
            enabled={autoBackup.enabled} 
            onClick={() => setAutoBackup(prev => ({ ...prev, enabled: !prev.enabled }))}
          >
            <input 
              type="checkbox" 
              checked={autoBackup.enabled} 
              onChange={() => {}}
            />
            <span>{autoBackup.enabled ? '✅ 自动备份已启用' : '⏸️ 自动备份已禁用'}</span>
          </Toggle>

          {autoBackup.enabled && (
            <>
              <SettingsGrid style={{ marginTop: '1.5rem' }}>
                <FormGroup>
                  <Label>备份频率</Label>
                  <Select 
                    value={autoBackup.schedule} 
                    onChange={(e) => setAutoBackup(prev => ({ ...prev, schedule: e.target.value }))}
                  >
                    <option value="daily">每天</option>
                    <option value="weekly">每周</option>
                    <option value="monthly">每月</option>
                  </Select>
                </FormGroup>

                {autoBackup.schedule === 'weekly' && (
                  <FormGroup>
                    <Label>选择星期</Label>
                    <Select 
                      value={autoBackup.dayOfWeek} 
                      onChange={(e) => setAutoBackup(prev => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                    >
                      <option value={0}>星期日</option>
                      <option value={1}>星期一</option>
                      <option value={2}>星期二</option>
                      <option value={3}>星期三</option>
                      <option value={4}>星期四</option>
                      <option value={5}>星期五</option>
                      <option value={6}>星期六</option>
                    </Select>
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>备份时间（时）</Label>
                  <Select 
                    value={autoBackup.hour} 
                    onChange={(e) => setAutoBackup(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>备份保留天数</Label>
                  <Select 
                    value={autoBackup.retentionDays} 
                    onChange={(e) => setAutoBackup(prev => ({ ...prev, retentionDays: parseInt(e.target.value) }))}
                  >
                    <option value={7}>7天</option>
                    <option value={14}>14天</option>
                    <option value={30}>30天</option>
                    <option value={60}>60天</option>
                    <option value={90}>90天</option>
                    <option value={0}>不自动删除</option>
                  </Select>
                </FormGroup>
              </SettingsGrid>

              <NextBackupInfo>
                <div>📅 <strong>上次备份：</strong>{autoBackup.lastBackup ? formatDate(autoBackup.lastBackup) : '暂无记录'}</div>
                <div style={{ marginTop: '0.5rem' }}>🕐 <strong>下次备份：</strong>{autoBackup.nextBackup ? formatDate(autoBackup.nextBackup) : '未设置'}</div>
              </NextBackupInfo>
            </>
          )}

          <ButtonGroup>
            <SecondaryButton onClick={handleTriggerBackup} disabled={backupLoading}>
              {backupLoading ? '备份中...' : '🚀 立即备份'}
            </SecondaryButton>
            <PrimaryButton onClick={handleSaveAutoBackup} disabled={savingSettings}>
              {savingSettings ? '保存中...' : '💾 保存设置'}
            </PrimaryButton>
          </ButtonGroup>
        </Section>

        <ActionBar>
          <ActionButton primary onClick={handleBackup} disabled={backupLoading}>
            {backupLoading ? '⏳ 备份中...' : '📥 一键备份'}
          </ActionButton>
        </ActionBar>

        <InfoBox>
          <h4>💡 使用说明</h4>
          <ul>
            <li><strong>一键备份</strong>：将所有数据导出为 JSON 文件保存到服务器</li>
            <li><strong>下载备份</strong>：将备份文件下载到本地保存</li>
            <li><strong>恢复数据</strong>：将备份文件的数据恢复到数据库（支持替换或合并模式）</li>
            <li><strong>建议</strong>：重要操作前请先下载备份，定期进行数据备份</li>
          </ul>
        </InfoBox>

        <TableContainer>
          {loading ? (
            <LoadingSpinner>加载中...</LoadingSpinner>
          ) : backups.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <div>暂无备份记录</div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>点击"一键备份"创建第一个备份</div>
            </EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>文件名</th>
                  <th>大小</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.name}>
                    <td>{backup.name}</td>
                    <td>{formatFileSize(backup.size)}</td>
                    <td>{formatDate(backup.createdAt)}</td>
                    <td>
                      <ActionButtons>
                        <SmallButton onClick={() => handleShowDetail(backup.name)}>详情</SmallButton>
                        <SmallButton onClick={() => handleDownload(backup.name)}>下载</SmallButton>
                        <SmallButton warning onClick={() => setRestoreModal(backup.name)}>恢复</SmallButton>
                        <SmallButton danger onClick={() => handleDelete(backup.name)}>删除</SmallButton>
                      </ActionButtons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableContainer>

        {/* 恢复确认弹窗 */}
        {restoreModal && (
          <Modal onClick={() => setRestoreModal(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>⚠️ 确认恢复数据</ModalTitle>
              <ModalText>
                您即将恢复备份文件 <strong>{restoreModal}</strong>
              </ModalText>
              
              <RestoreOptions>
                <RadioOption>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="replace"
                    checked={restoreMode === 'replace'}
                    onChange={() => setRestoreMode('replace')}
                  />
                  <div>
                    <strong>替换模式</strong>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>清空现有数据，用备份数据完全替换</div>
                  </div>
                </RadioOption>
                <RadioOption>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === 'merge'}
                    onChange={() => setRestoreMode('merge')}
                  />
                  <div>
                    <strong>合并模式</strong>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>保留现有数据，添加备份中不存在的新数据</div>
                  </div>
                </RadioOption>
              </RestoreOptions>
              
              <ModalButtons>
                <CancelButton onClick={() => setRestoreModal(null)}>取消</CancelButton>
                <ConfirmButton danger onClick={handleRestore} disabled={restoring}>
                  {restoring ? '恢复中...' : '确认恢复'}
                </ConfirmButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}

        {/* 详情弹窗 */}
        {detailModal && selectedBackup && (
          <Modal onClick={() => setDetailModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>📋 备份详情</ModalTitle>
              
              <InfoBox style={{ marginBottom: '1rem' }}>
                <h4>基本信息</h4>
                <ul style={{ fontSize: '0.85rem' }}>
                  <li><strong>文件名：</strong>{selectedBackup.filename}</li>
                  <li><strong>备份时间：</strong>{formatDate(selectedBackup.backupDate)}</li>
                  <li><strong>数据库：</strong>{selectedBackup.database}</li>
                  <li><strong>总记录数：</strong>{selectedBackup.totalRecords}</li>
                </ul>
              </InfoBox>
              
              <h4 style={{ margin: '1rem 0 0.5rem', color: '#333' }}>集合详情</h4>
              <CollectionInfo>
                {selectedBackup.collections.map((col) => (
                  <CollectionItem key={col.name}>
                    <span>{col.name}</span>
                    <span style={{ color: '#667eea', fontWeight: '500' }}>{col.count} 条</span>
                  </CollectionItem>
                ))}
              </CollectionInfo>
              
              <ModalButtons style={{ marginTop: '1.5rem' }}>
                <CancelButton onClick={() => setDetailModal(false)}>关闭</CancelButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </PageContainer>
    </AdminLayout>
  );
};

export default BackupPage;
